import { IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import React from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router';
import { ApplicatorPage } from './components/Applicators/ApplicatorPage';
import { BranchPage } from './components/Branches/BranchPage';
import { OrderSettingsPage } from './components/Businesses/OrderSettingsPage';
import { PurchaseOrderSettingsPage } from './components/Businesses/PurchaseOrderSettingsPage';
import { EmailSettingsPage } from './components/EmailSettings/EmailSettingsPage';
import { CarrierPage } from './components/Carriers/CarrierPage';
import { CustomerPage } from './components/Customers/CustomerPage';
import { BulkProductAllocationPage } from './components/Dashboard/BulkProductAllocationPage';
import { DwellTimePage } from './components/Dashboard/DwellTime/DwellTimePage';
import { IntegrationOrderExportStatusPage } from './components/Dashboard/IntegrationOrderExportStatusPage';
import { ProductLoadedPage } from './components/Dashboard/ProductLoadedPage';
import { SitesStatusPage } from './components/Dashboard/SitesStatusPage';
import { TankLevelPage } from './components/Dashboard/TankLevel/TankLevelPage';
import { DriverPage } from './components/Drivers/DriverPage';
import { ApiEventLogPage } from './components/ExternalIntegrations/ApiEventLogPage';
import { ExternalIntegrationsPage } from './components/ExternalIntegrations/ExternalIntegrationsPage';
import { Home } from './components/Home';
import { BusinessPage } from './components/KaEmployee/BusinessPage';
import { EmployeeOrderPage } from './components/KaEmployee/EmployeeOrderPage';
import { EmployeeSitesStatusPage } from './components/KaEmployee/EmployeeSitesStatusPage';
import { SiteNotificationPage } from './components/KaEmployee/SiteNotificationPage';
import { KahlerLogin } from './components/KahlerLogin';
import { Layout } from './components/Layout';
import { MonitoringContextProvider, withMonitoring } from './components/Monitoring/Monitoring';
import { OrderPage } from './components/Orders/OrderPage';
import { OrderTransactionsListPage } from './components/Orders/Transactions/OrderTransactionsListPage';
import { ProductPage } from './components/Products/ProductPage';
import PurchaseOrderPage from './components/PurchaseOrders/PurchaseOrderPage';
import SuppliersPage from './components/PurchaseOrders/SuppliersPage';
import { RecipePage } from './components/Recipes/RecipePage';
import { ReportsPage } from './components/Reports/ReportsPage';
import { SitePage } from './components/Sites/SitePage';
import { SiteRegistration } from './components/Sites/SiteRegistrationPage';
import { TransportPage } from './components/Transports/TransportPage';
import { TransportSyncPage } from './components/Transports/Sync/TransportSyncPage';
import BusinessAdminTemplate from './components/UserAccess/BusinessAdminTemplate';
import BusinessTemplate from './components/UserAccess/BusinessTemplate';
import { KaEmployeeTemplate } from './components/UserAccess/KaEmployeeTemplate';
import { UserProvider } from './components/UserAccess/UserProvider';
import { UserTemplate } from './components/UserAccess/UserTemplate';
import UserAgreementModal from './components/UserAgreementModal';
import { UserPage } from './components/Users/UserPage';
import OtherIntegrationsPage from './components/KaEmployee/OtherIntegrationsPage';
import { DataSharingPage } from './components/DataSharing/DataSharingPage';
import { AcceptConnectionPage } from './components/DataSharing/AcceptConnectionPage';
import { SharedProductsPage } from './components/SharedProducts/SharedProductsPage';
import { FieldAlyticsSiteMappingPage } from './components/FieldAlytics/SiteMappingPage';
import { FieldAlyticsProductMappingPage } from './components/FieldAlytics/ProductMappingPage';
import { FieldAlyticsOrderPage } from './components/FieldAlytics/OrderPage';
import { FieldAlyticsSettingsPage } from './components/FieldAlytics/SettingsPage';
import { FieldAlyticsApiEventLogPage } from './components/FieldAlytics/ApiEventLogPage';
import RequiresPermissionTemplate from './components/UserAccess/RequiresPermissionTemplate';
import { usePermission } from './components/UserAccess/usePermission';

const HomeRoute = () =>
  usePermission('dashboardReportsView') ? <Home /> : <Navigate to='/orders' replace />;

const UserLayout = () =>
  <UserTemplate>
    <UserAgreementModal />
    <BusinessTemplate>
      <Outlet />
    </BusinessTemplate>
  </UserTemplate>

const KaEmployeeLayout = () =>
  <KaEmployeeTemplate><Outlet /></KaEmployeeTemplate>

export const AppRoutes = () =>
  <Routes>
    <Route path='/kahlerLogin' element={<KahlerLogin />} />
    <Route path='/siteRegistration' element={<SiteRegistration />} />
    <Route path='/dataSharing/acceptConnection/:code' element={<AcceptConnectionPage />} />
    <Route element={<UserLayout />}>
      <Route path='/' element={<HomeRoute />} />
      <Route element={<BusinessAdminTemplate><Outlet /></BusinessAdminTemplate>}>
        <Route path='/users' element={<UserPage />} />
        <Route path='/sites' element={<SitePage />} />
      </Route>
      <Route path='/customers' element={<CustomerPage />} />
      <Route path='/branches' element={<BranchPage />} />
      <Route path='/drivers' element={<DriverPage />} />
      <Route path='/carriers' element={<CarrierPage />} />
      <Route path='/applicators' element={<ApplicatorPage />} />
      <Route element={<RequiresPermissionTemplate permission='settingsModify'><Outlet /></RequiresPermissionTemplate>}>
        <Route path='/datasharing' element={<DataSharingPage />} />
        <Route path='/sharedproducts' element={<SharedProductsPage />} />
        <Route path='/ordersettings' element={<OrderSettingsPage />} />
        <Route path='/purchaseOrdersettings' element={<PurchaseOrderSettingsPage />} />
        <Route path='/emailsettings' element={<EmailSettingsPage />} />
      </Route>
      <Route path='/products' element={<ProductPage />} />
      <Route path='/recipes' element={<RecipePage />} />
      <Route path='/orders/:completed?/:orderNumber?' element={<OrderPage />} />
      <Route path='/orderTransactions' element={<OrderTransactionsListPage />} />
      <Route path='/purchaseOrders' element={<PurchaseOrderPage />} />
      <Route path='/suppliers' element={<SuppliersPage />} />
      <Route path='/transports' element={<TransportPage />} />
      <Route path='/transports/sync' element={<TransportSyncPage />} />
      <Route element={<RequiresPermissionTemplate permission='integrationsModify'><Outlet /></RequiresPermissionTemplate>}>
        <Route path='/extIntegrations' element={<ExternalIntegrationsPage />} />
        <Route path='/extIntegrations/apiEventLog' element={<ApiEventLogPage />} />
      </Route>
      <Route element={<RequiresPermissionTemplate permission='dashboardReportsView'><Outlet /></RequiresPermissionTemplate>}>
        <Route path='/reports' element={<ReportsPage />} />
        <Route path='/productLoaded' element={<ProductLoadedPage />} />
        <Route path='/sitesStatus' element={<SitesStatusPage />} />
        <Route path='/integrationOrderExportStatus' element={<IntegrationOrderExportStatusPage />} />
        <Route path='/bulkProductAllocation' element={<BulkProductAllocationPage />} />
        <Route path='/dwellTime' element={<DwellTimePage />} />
        <Route path='/tankLevelMonitoring' element={<TankLevelPage />} />
      </Route>
    </Route>
    <Route path='/fieldalytics' element={<UserLayout />}>
      <Route element={<RequiresPermissionTemplate permission='integrationsModify'><Outlet /></RequiresPermissionTemplate>}>
        <Route path='siteMapping' element={<FieldAlyticsSiteMappingPage />} />
        <Route path='productMapping' element={<FieldAlyticsProductMappingPage />} />
        <Route path='orders' element={<FieldAlyticsOrderPage />} />
        <Route path='apiEventLog' element={<FieldAlyticsApiEventLogPage />} />
        <Route path='settings' element={<FieldAlyticsSettingsPage />} />
      </Route>
    </Route>
    <Route path='/employee' element={<KaEmployeeLayout />}>
      <Route path='business' element={<BusinessPage />} />
      <Route path='sitesStatus' element={<EmployeeSitesStatusPage />} />
      <Route path='siteNotifications' element={<SiteNotificationPage />} />
      <Route path='orders' element={<EmployeeOrderPage />} />
      <Route path='integrations' element={<OtherIntegrationsPage />} />
      <Route path='fieldalytics'>
        <Route path='siteMapping' element={<FieldAlyticsSiteMappingPage />} />
        <Route path='productMapping' element={<FieldAlyticsProductMappingPage />} />
        <Route path='orders' element={<FieldAlyticsOrderPage />} />
        <Route path='apiEventLog' element={<FieldAlyticsApiEventLogPage />} />
        <Route path='settings' element={<FieldAlyticsSettingsPage />} />
      </Route>
      <Route path='integrations/apiEventLog' element={<ApiEventLogPage />} />
    </Route>
  </Routes>;

const App = ({ pca }: { pca: IPublicClientApplication }) => {
  return (
    <MsalProvider instance={pca}>
      <MonitoringContextProvider>
        <UserProvider>
          <Layout>
            <AppRoutes />
          </Layout>
        </UserProvider>
      </MonitoringContextProvider>
    </MsalProvider>
  );
}

export default withMonitoring(App);
